import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import { Container, Row, Col } from 'react-bootstrap';
import { decamelizeKeys } from 'humps';
import cx from 'classnames';

import Step from './Step';
import Input from '../Forms/Input';
import RadioButtonGroup from '../Forms/RadioButtonGroup';
import API from '../../services/API';
import Button from '../UI/Button';
import Tabs from '../UI/Tabs';
import * as formValidators from '../../utils/formValidation';
import { withTranslation } from '../../i18n';
import styles from './styles.css';

class StepAbout extends PureComponent {
  state = {
    isSubmitting: false,
  };

  handleSubmit = async formValues => {
    const { crewType, goToNextStep, setServiceProfile } = this.props;

    this.setState({ isSubmitting: true });

    const values = {
      ...formValues,
      crewType,
    };

    const updatedServiceProfile = await API.fetch(undefined, '/user/service-profile/update', decamelizeKeys(values));

    goToNextStep();
    this.setState({ isSubmitting: false });
    setServiceProfile(updatedServiceProfile);
  };

  getInitialValues() {
    const { serviceProfile, t } = this.props;

    if (!serviceProfile) {
      return {};
    }

    return {
      ...serviceProfile,
      isExParticipant: serviceProfile.isExParticipant ? 'yes' : 'no',
      hasDriversLicense: serviceProfile.hasDriversLicense ? 'yes' : 'no',
      driversLicenseType: serviceProfile.driversLicenseType || 'B',
      hasCar: serviceProfile.hasCar ? 'yes' : 'no',
      tshirtSize: serviceProfile.tshirtSize || '3',
      brandAffinities: [
        serviceProfile.brandAffinityParty && 'party',
        serviceProfile.brandAffinityAdventure && 'adventure',
        serviceProfile.brandAffinityRally && 'rally',
      ].filter(Boolean),
    };
  }

  validateForm = formValues => {
    const { t } = this.props;
    const errors = {};

    formValidators.required(formValues, errors, 'isExParticipant');
    formValidators.required(formValues, errors, 'hasDriversLicense');
    formValidators.required(formValues, errors, 'hasCar');
    formValidators.required(formValues, errors, 'tshirtSize');
    formValidators.required(formValues, errors, 'motivation');

    if (formValues.hasDriversLicense === 'yes') {
      formValidators.required(formValues, errors, 'driversLicenseType');
    }

    return errors;
  };

  render() {
    const { visible, t, goToPreviousStep, serviceProfile, i18n } = this.props;
    const { isSubmitting } = this.state;

    return (
      <Step visible={visible}>
        <Form
          onSubmit={this.handleSubmit}
          initialValues={this.getInitialValues()}
          validate={this.validateForm}
          render={({ handleSubmit, pristine, invalid, values }) => (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <Field
                  component={RadioButtonGroup}
                  name="isExParticipant"
                  label={t('isExParticipant')}
                  required
                  options={[{ value: 'yes', label: t('yes') }, { value: 'no', label: t('no') }]}
                />
              </div>
              <div className="mb-3">
                <Field
                  component={RadioButtonGroup}
                  name="hasDriversLicense"
                  label={t('hasDriversLicense')}
                  required
                  options={[{ value: 'yes', label: t('yes') }, { value: 'no', label: t('no') }]}
                />
              </div>
              {values.hasDriversLicense === 'yes' && (
                <div className="mb-3">
                  <Field
                    component={Input}
                    as="select"
                    name="driversLicenseType"
                    label={t('driversLicenseType')}
                    required
                  >
                    <option value="B">{t('driversLicenseTypes.B')}</option>
                    <option value="BE">{t('driversLicenseTypes.BE')}</option>
                    <option value="C">{t('driversLicenseTypes.C')}</option>
                    <option value="G">{t('driversLicenseTypes.G')}</option>
                  </Field>
                </div>
              )}
              <div className="mb-3">
                <Field
                  component={RadioButtonGroup}
                  name="hasCar"
                  label={t('hasCar')}
                  required
                  options={[{ value: 'yes', label: t('yes') }, { value: 'no', label: t('no') }]}
                />
              </div>
              <div className="mb-3">
                <Field component={Input} as="select" label={t('tshirtSize')} name="tshirtSize" required>
                  <option value="1">XS</option>
                  <option value="2">S</option>
                  <option value="3">M</option>
                  <option value="4">L</option>
                  <option value="5">XL</option>
                  <option value="6">XXL</option>
                </Field>
              </div>
              <div className="mb-3">
                <Field
                  component={Input}
                  as="textarea"
                  rows="3"
                  name="otherPeopleYouKnow"
                  label={t('otherPeopleYouKnow')}
                />
              </div>
              <div className="mb-3">
                <Field
                  component={Input}
                  as="textarea"
                  rows="3"
                  name="motivation"
                  label={t('yourMotivation')}
                  required
                />
              </div>

              <label>{t('brandAffinity')}</label>
              <div className="mb-3">
                <div>
                  <label>
                    <Field component="input" type="checkbox" name="brandAffinities[]" value="party" />{' '}
                    {t('brandAffinities.party')}
                  </label>
                </div>
                <div>
                  <label>
                    <Field component="input" type="checkbox" name="brandAffinities[]" value="adventure" />{' '}
                    {t('brandAffinities.adventure')}
                  </label>
                </div>
                <div>
                  <label>
                    <Field component="input" type="checkbox" name="brandAffinities[]" value="rally" />{' '}
                    {t('brandAffinities.rally')}
                  </label>
                </div>
              </div>

              <div className={cx('mt-5', styles.formButtons)}>
                <Button type="button" level="secondary" onClick={goToPreviousStep} className="mr-3">
                  {t('back')}
                </Button>
                <Button type="submit" processing={isSubmitting}>
                  {t('next')}
                </Button>
              </div>
            </form>
          )}
        />
      </Step>
    );
  }
}

StepAbout.propTypes = {
  visible: PropTypes.bool,
  goToPreviousStep: PropTypes.func.isRequired,
  goToNextStep: PropTypes.func.isRequired,
  setServiceProfile: PropTypes.func,
  serviceProfile: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation('common')(StepAbout);
